// src/cockpit4/components/DsoDecisionEngine.tsx
// ============================================
// DSO 决策层重构组件 —— GEO+DSO 商业化改造 第 3 层
//
// 背景（XEAOS_详细改进执行计划_v2.0 第 9.1 节 特别优化要求）：
//   将现有 DSO 从"固定模板产出 3 篇通用文章"升级为"多平台搜索卡位分发中枢"。
//   解决三篇内容数据完全一致的灌水问题，转向企业资产绑定 + 多平台差异化分发。
//
// 三大模块：
//   模块1 - 内容梯队：三类内容梯队（品牌深度 / 业务精准 / 行业痛点），
//           绑定企业资产与 GEO 关键词，不再单纯蹭外部热点。
//   模块2 - 矩阵分发：5 平台 × 主/子账号矩阵布局，差异化分发策略 + 收录检测 + 自动补发。
//   模块3 - 竞品监测：拆解竞品爆款搜索内容，自动生成差异化对标内容抢占搜索位置。
//
// 数据来源（禁止假数据）：
//   1. 内容梯队 / 分发任务 / 竞品监测 → localStorage（key: xeaos_dso_decision）
//      全部由用户人工录入或 AI 生成，无数据时显示空状态。
//   2. GEO 关键词与企业资产 → localStorage（key: xeaos_geo_input）联动读取。
//   3. 内容生成 → RuntimeManager → MediaStudio（generate_custom 文本生成）
//
// 改造目标：让 XEAOS 从"3 篇灌水文章"升级为"多平台搜索卡位分发中枢"，
//          实现品牌深度 + 业务精准 + 行业痛点三层内容矩阵的精准分发。
// ============================================

import { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Layers,
  Share2,
  Swords,
  Plus,
  Trash2,
  Sparkles,
  Send,
  RefreshCw,
  Loader2,
  CheckCircle2,
  XCircle,
  Search,
  Eye,
  Zap,
  TrendingUp,
  Award,
  Target,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  FileText,
  Image,
  Video,
  HelpCircle,
  Users,
  Bookmark,
  RotateCw,
  BarChart3,
  Hash,
  Clock,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";

// ============================================
// 类型定义（来自改进计划文档 9.1 第三层改造数据模型）
// ============================================

/** 内容梯队类型 */
type ContentTier = "brand_deep" | "business_precise" | "industry_pain";

/** 分发平台 */
type DsoPlatform = "wechat_official" | "xiaohongshu" | "douyin" | "zhihu" | "bilibili";

/** 内容状态 */
type ContentStatus =
  | "planned"
  | "generating"
  | "generated"
  | "published"
  | "indexed"
  | "not_indexed"
  | "failed";

/** 内容形式 */
type ContentFormat = "long_article" | "short_article" | "image_text" | "video_script" | "qa";

/** 平台分发计划 */
interface PlatformPlan {
  platform: DsoPlatform;
  accountType: "main" | "sub"; // 主账号 / 子账号
  differentiatedAngle: string; // 差异化角度
  titleTemplate: string; // 标题模板
  contentStyle: string; // 内容风格
  publishTiming: string; // 发布时段
  publishFrequency: string; // 发布频率
}

/** 内容计划 */
interface ContentPlan {
  id: string;
  tier: ContentTier;
  title: string;
  targetKeywords: string[]; // 关联关键词（从 GEO 读取）
  brandAssets: string[]; // 关联企业资产
  format: ContentFormat;
  platformPlans: PlatformPlan[];
  status: ContentStatus;
  generatedContent?: string;
  publishedAt?: string;
  indexedStatus?: Record<DsoPlatform, "indexed" | "not_indexed" | "pending">;
  views?: number;
  leads?: number;
  createdAt: string;
}

/** 矩阵账号 */
interface MatrixAccount {
  platform: DsoPlatform;
  accountType: "main" | "sub";
  accountName: string;
  followers: number;
  authority: number; // 账号权重 0-100
  contentCount: number;
  avgViews: number;
}

/** 竞品爆款内容 */
interface CompetitorHitContent {
  title: string;
  titleStructure: string; // 标题结构分析
  keywordLayout: string[]; // 关键词布局
  views: number;
  engagement: number;
}

/** 差异化对标内容 */
interface CounterContent {
  title: string;
  differentiation: string; // 差异化点
  status: "planned" | "generated";
}

/** 竞品 DSO 监测 */
interface CompetitorDso {
  id: string;
  competitorName: string;
  platform: DsoPlatform;
  hitContent: CompetitorHitContent[];
  counterContent?: CounterContent[];
}

/** DSO 决策层持久化状态 */
interface DsoDecisionState {
  contentPlans: ContentPlan[];
  matrixAccounts: MatrixAccount[];
  competitors: CompetitorDso[];
}

/** GEO 关键词引用（从 xeaos_geo_input 读取） */
interface GeoKeywordRef {
  id: string;
  keyword: string;
  tier: string;
  searchVolume?: number;
  difficulty?: number;
}

// ============================================
// 常量配置
// ============================================

const STORAGE_KEY = "xeaos_dso_decision";
const GEO_STORAGE_KEY = "xeaos_geo_input";

/** DSO 主色调：橙红色系（决策层） */
const DSO_COLOR = {
  primary: "#F97316", // 橙色
  secondary: "#EF4444", // 红色
  accent: "#FB923C", // 浅橙
  gradient: "linear-gradient(135deg, #F97316 0%, #EF4444 100%)",
};

/** 三类内容梯队元数据 */
const CONTENT_TIER_META: Record<
  ContentTier,
  { label: string; color: string; frequency: string; desc: string; icon: typeof Award }
> = {
  brand_deep: {
    label: "品牌深度",
    color: "#A855F7",
    frequency: "每月2-4篇",
    desc: "企业故事/产品深度/案例研究/客户证言",
    icon: Award,
  },
  business_precise: {
    label: "业务精准",
    color: "#3B82F6",
    frequency: "每周2-3篇",
    desc: "服务介绍/解决方案/报价体系/地域服务能力",
    icon: Target,
  },
  industry_pain: {
    label: "行业痛点",
    color: "#10B981",
    frequency: "每日1-2篇",
    desc: "问题解答/避坑指南/对比测评/教程干货",
    icon: TrendingUp,
  },
};

/** 5 平台显示配置 */
const PLATFORM_META: Record<
  DsoPlatform,
  { name: string; color: string; icon: string; charLimit: number; algorithm: string }
> = {
  wechat_official: {
    name: "微信公众号",
    color: "#07C160",
    icon: "💬",
    charLimit: 20000,
    algorithm: "社交推荐 + 搜索召回，长文深度权重高",
  },
  xiaohongshu: {
    name: "小红书",
    color: "#FF2442",
    icon: "📕",
    charLimit: 1000,
    algorithm: "笔记搜索卡位 + 标签召回，图文高频更新",
  },
  douyin: {
    name: "抖音",
    color: "#000000",
    icon: "🎵",
    charLimit: 300,
    algorithm: "搜索 SEO + 话题流量，短视频脚本卡位",
  },
  zhihu: {
    name: "知乎",
    color: "#0084FF",
    icon: "💡",
    charLimit: 100000,
    algorithm: "问答搜索 + 长尾覆盖，专业干货权重高",
  },
  bilibili: {
    name: "B站",
    color: "#FB7299",
    icon: "📺",
    charLimit: 20000,
    algorithm: "搜索分区 + 标签召回，视频专栏双轨",
  },
};

/** 内容状态元数据 */
const STATUS_META: Record<ContentStatus, { label: string; color: string; icon: typeof Clock }> = {
  planned: { label: "已计划", color: "#6B7280", icon: FileText },
  generating: { label: "生成中", color: "#F59E0B", icon: Loader2 },
  generated: { label: "已生成", color: "#3B82F6", icon: CheckCircle2 },
  published: { label: "已发布", color: "#10B981", icon: Send },
  indexed: { label: "已收录", color: "#22C55E", icon: Bookmark },
  not_indexed: { label: "未收录", color: "#EF4444", icon: AlertCircle },
  failed: { label: "失败", color: "#DC2626", icon: XCircle },
};

/** 内容形式元数据 */
const FORMAT_META: Record<ContentFormat, { label: string; icon: typeof FileText }> = {
  long_article: { label: "深度长文", icon: FileText },
  short_article: { label: "短文", icon: FileText },
  image_text: { label: "图文笔记", icon: Image },
  video_script: { label: "视频脚本", icon: Video },
  qa: { label: "问答", icon: HelpCircle },
};

/** 账号类型元数据 */
const ACCOUNT_TYPE_META: Record<"main" | "sub", { label: string; color: string; desc: string }> = {
  main: { label: "主账号", color: "#F97316", desc: "深度干货，树立品牌形象" },
  sub: { label: "子账号", color: "#FB923C", desc: "海量长尾词笔记，卡位搜索" },
};

/** 空状态 */
const EMPTY_STATE: DsoDecisionState = {
  contentPlans: [],
  matrixAccounts: [],
  competitors: [],
};

const ALL_PLATFORMS: DsoPlatform[] = [
  "wechat_official",
  "xiaohongshu",
  "douyin",
  "zhihu",
  "bilibili",
];

// ============================================
// 工具函数
// ============================================

/** 生成唯一 ID */
function genId(): string {
  return `dso_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

/** 加载 DSO 决策层数据 */
function loadState(): DsoDecisionState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY_STATE;
    const parsed = JSON.parse(raw) as Partial<DsoDecisionState>;
    return {
      contentPlans: Array.isArray(parsed.contentPlans) ? parsed.contentPlans : [],
      matrixAccounts: Array.isArray(parsed.matrixAccounts) ? parsed.matrixAccounts : [],
      competitors: Array.isArray(parsed.competitors) ? parsed.competitors : [],
    };
  } catch {
    return EMPTY_STATE;
  }
}

/** 保存 DSO 决策层数据 */
function saveState(state: DsoDecisionState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // 存储失败静默处理
  }
}

/** 从 GEO 输入层读取关键词和企业资产 */
function loadGeoData(): { keywords: GeoKeywordRef[]; brandAssets: string[] } {
  try {
    const raw = localStorage.getItem(GEO_STORAGE_KEY);
    if (!raw) return { keywords: [], brandAssets: [] };
    const parsed = JSON.parse(raw) as any;
    // 读取四级关键词体系
    const keywords: GeoKeywordRef[] = Array.isArray(parsed.keywords)
      ? parsed.keywords.map((k: any) => ({
          id: k.id || genId(),
          keyword: k.keyword || "",
          tier: k.tier || "",
          searchVolume: k.searchVolume,
          difficulty: k.difficulty,
        }))
      : [];
    // 读取品牌资产池（品牌全称 / 简称 / 产品名 / 创始人 IP）
    const brandPool = parsed.brandPool || {};
    const brandAssets: string[] = [
      brandPool.brandFullName,
      brandPool.brandShortName,
      ...(Array.isArray(brandPool.productNames) ? brandPool.productNames : []),
      ...(Array.isArray(brandPool.founderIpNames) ? brandPool.founderIpNames : []),
    ].filter(Boolean);
    return { keywords, brandAssets };
  } catch {
    return { keywords: [], brandAssets: [] };
  }
}

/** 根据梯队和平台自动生成差异化分发策略 */
function autoPlatformPlan(
  tier: ContentTier,
  platform: DsoPlatform,
  accountType: "main" | "sub"
): PlatformPlan {
  const tierMeta = CONTENT_TIER_META[tier];
  const platMeta = PLATFORM_META[platform];
  // 按梯队 × 平台生成差异化角度
  const angleMap: Record<ContentTier, Record<DsoPlatform, string>> = {
    brand_deep: {
      wechat_official: "品牌故事长文，深度叙事建立信任",
      xiaohongshu: "品牌创始人 IP 笔记，品牌调性图文",
      douyin: "品牌大片短视频，情感共鸣脚本",
      zhihu: "品牌深度专栏，行业观点输出",
      bilibili: "品牌纪录片式视频，故事化叙事",
    },
    business_precise: {
      wechat_official: "服务方案详解长文，报价体系透明化",
      xiaohongshu: "服务种草笔记，地域+服务关键词卡位",
      douyin: "服务流程拆解短视频，口播+字幕",
      zhihu: "专业方案回答，长尾问题覆盖",
      bilibili: "服务评测视频，对比展示",
    },
    industry_pain: {
      wechat_official: "避坑指南干货长文，问题汇总解答",
      xiaohongshu: "高频长尾词笔记，每日卡位更新",
      douyin: "痛点解答短视频，口播干货",
      zhihu: "问答卡位，长尾问题批量覆盖",
      bilibili: "教程干货视频，步骤拆解",
    },
  };
  const styleMap: Record<ContentTier, string> = {
    brand_deep: "深度叙事、情感共鸣、品牌调性",
    business_precise: "专业严谨、数据支撑、转化导向",
    industry_pain: "通俗易懂、痛点直击、高频更新",
  };
  const timingMap: Record<DsoPlatform, string> = {
    wechat_official: "工作日 8:00 / 12:00 / 20:00",
    xiaohongshu: "每日 7:00 / 12:00 / 18:00 / 22:00",
    douyin: "每日 12:00 / 18:00 / 21:00",
    zhihu: "工作日 10:00 / 15:00 / 21:00",
    bilibili: "周末 10:00 / 20:00",
  };
  const freqMap: Record<"main" | "sub", string> = {
    main: tier === "brand_deep" ? "每月2-4篇" : tier === "business_precise" ? "每周1篇" : "每周3篇",
    sub: tier === "industry_pain" ? "每日1-2篇" : "每周2-3篇",
  };
  return {
    platform,
    accountType,
    differentiatedAngle: angleMap[tier][platform],
    titleTemplate: `${platMeta.icon} ${tierMeta.label} | {关键词} - {品牌资产}`,
    contentStyle: styleMap[tier],
    publishTiming: timingMap[platform],
    publishFrequency: freqMap[accountType],
  };
}

/** 计算收录率 */
function calcIndexRate(plans: ContentPlan[]): number {
  const published = plans.filter((p) => p.status === "published" || p.status === "indexed" || p.status === "not_indexed");
  if (published.length === 0) return 0;
  let totalPlatforms = 0;
  let indexedCount = 0;
  published.forEach((p) => {
    if (p.indexedStatus) {
      ALL_PLATFORMS.forEach((plat) => {
        if (p.indexedStatus![plat]) {
          totalPlatforms++;
          if (p.indexedStatus![plat] === "indexed") indexedCount++;
        }
      });
    }
  });
  return totalPlatforms === 0 ? 0 : Math.round((indexedCount / totalPlatforms) * 100);
}

// ============================================
// 主组件
// ============================================

export default function DsoDecisionEngine() {
  const [state, setState] = useState<DsoDecisionState>(EMPTY_STATE);
  const [geoKeywords, setGeoKeywords] = useState<GeoKeywordRef[]>([]);
  const [brandAssets, setBrandAssets] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<"tiers" | "matrix" | "competitor">("tiers");
  const [generatingIds, setGeneratingIds] = useState<Set<string>>(new Set());
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);
  const [expandedPlanId, setExpandedPlanId] = useState<string | null>(null);

  // 创建表单状态
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState<{
    tier: ContentTier;
    title: string;
    format: ContentFormat;
    selectedKeywords: string[];
    selectedAssets: string[];
    selectedPlatforms: DsoPlatform[];
    platformAccountTypes: Record<DsoPlatform, "main" | "sub">;
  }>({
    tier: "industry_pain",
    title: "",
    format: "long_article",
    selectedKeywords: [],
    selectedAssets: [],
    selectedPlatforms: ["xiaohongshu"],
    platformAccountTypes: {
      wechat_official: "main",
      xiaohongshu: "sub",
      douyin: "sub",
      zhihu: "main",
      bilibili: "main",
    },
  });

  // 矩阵账号编辑
  const [showMatrixForm, setShowMatrixForm] = useState(false);
  const [matrixForm, setMatrixForm] = useState<{
    platform: DsoPlatform;
    accountType: "main" | "sub";
    accountName: string;
    followers: number;
    authority: number;
  }>({
    platform: "xiaohongshu",
    accountType: "main",
    accountName: "",
    followers: 0,
    authority: 50,
  });

  // 竞品编辑
  const [showCompetitorForm, setShowCompetitorForm] = useState(false);
  const [competitorForm, setCompetitorForm] = useState<{
    competitorName: string;
    platform: DsoPlatform;
  }>({ competitorName: "", platform: "xiaohongshu" });

  // 竞品爆款内容编辑
  const [editingCompetitorId, setEditingCompetitorId] = useState<string | null>(null);
  const [hitContentForm, setHitContentForm] = useState<{
    title: string;
    titleStructure: string;
    keywordLayout: string;
    views: number;
    engagement: number;
  }>({ title: "", titleStructure: "", keywordLayout: "", views: 0, engagement: 0 });

  // ============================================
  // 初始化加载数据
  // ============================================
  useEffect(() => {
    const s = loadState();
    setState(s);
    const geo = loadGeoData();
    setGeoKeywords(geo.keywords);
    setBrandAssets(geo.brandAssets);
  }, []);

  // ============================================
  // 状态变更后自动持久化
  // ============================================
  const updateState = useCallback((updater: (prev: DsoDecisionState) => DsoDecisionState) => {
    setState((prev) => {
      const next = updater(prev);
      saveState(next);
      return next;
    });
  }, []);

  // toast 自动消失
  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  // ============================================
  // 内容生成：调用 MediaStudio generate_custom
  // ============================================
  const generateContent = useCallback(
    async (planId: string) => {
      const plan = state.contentPlans.find((p) => p.id === planId);
      if (!plan) return;

      setGeneratingIds((prev) => new Set(prev).add(planId));
      updateState((prev) => ({
        ...prev,
        contentPlans: prev.contentPlans.map((p) =>
          p.id === planId ? { ...p, status: "generating" } : p
        ),
      }));

      try {
        // 构建生成 Prompt（包含梯队定位、关键词、品牌资产、平台差异化要求）
        const tierMeta = CONTENT_TIER_META[plan.tier];
        const keywordStr = plan.targetKeywords.join("、") || "通用关键词";
        const assetStr = plan.brandAssets.join("、") || "品牌";
        const platformAngles = plan.platformPlans
          .map((pp) => `${PLATFORM_META[pp.platform].name}(${ACCOUNT_TYPE_META[pp.accountType].label}): ${pp.differentiatedAngle}`)
          .join("; ");
        const formatLabel = FORMAT_META[plan.format].label;

        const prompt = `你是一个专业的${tierMeta.label}内容创作者。
请围绕以下主题创作一篇${formatLabel}：
标题：${plan.title}
内容梯队：${tierMeta.label}（${tierMeta.desc}）
目标关键词：${keywordStr}
关联品牌资产：${assetStr}
分发平台差异化要求：${platformAngles}
内容风格：${plan.platformPlans[0]?.contentStyle || "专业严谨"}

要求：
1. 标题包含核心关键词，吸引点击
2. 内容自然植入品牌资产，不做硬广
3. 结构清晰，分段有小标题
4. 结尾设置引导互动或引流入口
5. 字数 ${plan.format === "long_article" ? "2000-3000字" : plan.format === "short_article" ? "800-1200字" : "500-1000字"}`;

        let generatedText = "";
        // 优先通过 RuntimeManager 调用 MediaStudio 生成内容
        if (RuntimeManager.isInitialized() && RuntimeManager.hasRuntime(RuntimeName.MediaStudio)) {
          const resp = await RuntimeManager.execute(RuntimeName.MediaStudio, "generate_custom", {
            type: "text",
            topic: plan.title,
            prompt,
            opts: { tier: plan.tier, format: plan.format },
          });
          const inner = resp?.data as any;
          if (resp?.success && inner) {
            generatedText =
              inner.content || inner.text || inner.lyrics || (typeof inner === "string" ? inner : "");
          }
        }

        // 如果 Runtime 未就绪或返回空，给出明确提示而非假数据
        if (!generatedText) {
          generatedText = `[内容生成未完成] RuntimeManager 未就绪或 MediaStudio 未返回有效内容。
请确保 XEAOS Runtime 已初始化后重新生成。

—— 生成参数 ——
梯队：${tierMeta.label}
标题：${plan.title}
关键词：${keywordStr}
品牌资产：${assetStr}
平台：${platformAngles}`;
        }

        updateState((prev) => ({
          ...prev,
          contentPlans: prev.contentPlans.map((p) =>
            p.id === planId
              ? { ...p, status: "generated", generatedContent: generatedText }
              : p
          ),
        }));
        setToast({ message: "内容生成完成", type: "success" });
      } catch (err) {
        updateState((prev) => ({
          ...prev,
          contentPlans: prev.contentPlans.map((p) =>
            p.id === planId ? { ...p, status: "failed" } : p
          ),
        }));
        setToast({ message: `生成失败: ${err instanceof Error ? err.message : "未知错误"}`, type: "error" });
      } finally {
        setGeneratingIds((prev) => {
          const next = new Set(prev);
          next.delete(planId);
          return next;
        });
      }
    },
    [state.contentPlans, updateState]
  );

  // ============================================
  // 发布内容：标记已发布 + 初始化收录状态
  // ============================================
  const publishPlan = useCallback(
    (planId: string) => {
      updateState((prev) => ({
        ...prev,
        contentPlans: prev.contentPlans.map((p) => {
          if (p.id !== planId) return p;
          const indexedStatus = {} as Record<DsoPlatform, "indexed" | "not_indexed" | "pending">;
          p.platformPlans.forEach((pp) => {
            indexedStatus[pp.platform] = "pending";
          });
          return {
            ...p,
            status: "published",
            publishedAt: new Date().toISOString(),
            indexedStatus,
          };
        }),
      }));
      setToast({ message: "内容已发布，收录状态待检测", type: "success" });
    },
    [updateState]
  );

  // ============================================
  // 收录检测：切换收录状态（用户手动校验后标记）
  // ============================================
  const toggleIndexStatus = useCallback(
    (planId: string, platform: DsoPlatform) => {
      updateState((prev) => ({
        ...prev,
        contentPlans: prev.contentPlans.map((p) => {
          if (p.id !== planId || !p.indexedStatus) return p;
          const current = p.indexedStatus[platform];
          const next: "indexed" | "not_indexed" | "pending" =
            current === "pending" ? "indexed" : current === "indexed" ? "not_indexed" : "pending";
          const newIndexedStatus = { ...p.indexedStatus, [platform]: next };
          // 综合状态：所有平台已收录 → indexed；有未收录 → not_indexed；否则保持 published
          const statuses = Object.values(newIndexedStatus);
          const allIndexed = statuses.every((s) => s === "indexed");
          const hasNotIndexed = statuses.some((s) => s === "not_indexed");
          let newStatus: ContentStatus = p.status;
          if (allIndexed) newStatus = "indexed";
          else if (hasNotIndexed) newStatus = "not_indexed";
          else newStatus = "published";
          return { ...p, indexedStatus: newIndexedStatus, status: newStatus };
        }),
      }));
    },
    [updateState]
  );

  // ============================================
  // 一键补发：对未收录内容重新生成优化版本
  // ============================================
  const repostNotIndexed = useCallback(
    async (planId: string) => {
      const plan = state.contentPlans.find((p) => p.id === planId);
      if (!plan || !plan.generatedContent) return;
      setGeneratingIds((prev) => new Set(prev).add(planId));
      try {
        // 重新生成优化版本，强调搜索收录优化
        const notIndexedPlatforms = plan.platformPlans
          .filter((pp) => plan.indexedStatus?.[pp.platform] === "not_indexed")
          .map((pp) => PLATFORM_META[pp.platform].name)
          .join("、");
        const optimizedPrompt = `以下是未被平台收录的内容，请优化重写以提升搜索收录率。
未收录平台：${notIndexedPlatforms}
原标题：${plan.title}
原内容：${plan.generatedContent.slice(0, 500)}...

优化要求：
1. 调整标题结构，增加搜索关键词密度
2. 优化开头100字，确保包含核心关键词
3. 增加小标题和结构化标签
4. 补充长尾关键词覆盖`;

        let optimized = "";
        if (RuntimeManager.isInitialized() && RuntimeManager.hasRuntime(RuntimeName.MediaStudio)) {
          const resp = await RuntimeManager.execute(RuntimeName.MediaStudio, "generate_custom", {
            type: "text",
            topic: `优化补发：${plan.title}`,
            prompt: optimizedPrompt,
            opts: { repost: true },
          });
          const inner = resp?.data as any;
          if (resp?.success && inner) {
            optimized = inner.content || inner.text || "";
          }
        }
        if (!optimized) {
          optimized = "[补发内容生成未完成] 请确保 Runtime 已初始化后重试。";
        }

        updateState((prev) => ({
          ...prev,
          contentPlans: prev.contentPlans.map((p) =>
            p.id === planId
              ? {
                  ...p,
                  generatedContent: optimized,
                  indexedStatus: Object.fromEntries(
                    Object.entries(p.indexedStatus || {}).map(([k, v]) => [
                      k,
                      v === "not_indexed" ? "pending" : v,
                    ])
                  ) as Record<DsoPlatform, "indexed" | "not_indexed" | "pending">,
                }
              : p
          ),
        }));
        setToast({ message: "补发内容已重新生成，收录状态重置为待检测", type: "success" });
      } finally {
        setGeneratingIds((prev) => {
          const next = new Set(prev);
          next.delete(planId);
          return next;
        });
      }
    },
    [state.contentPlans, updateState]
  );

  // ============================================
  // 生成竞品差异化对标内容
  // ============================================
  const generateCounterContent = useCallback(
    async (competitorId: string) => {
      const comp = state.competitors.find((c) => c.id === competitorId);
      if (!comp || comp.hitContent.length === 0) return;

      try {
        const hitTitles = comp.hitContent.map((h) => h.title).join("、");
        const keywords = comp.hitContent.flatMap((h) => h.keywordLayout).join("、");
        const prompt = `作为差异化内容策略师，请针对竞品爆款内容生成差异化对标内容。
竞品名称：${comp.competitorName}
平台：${PLATFORM_META[comp.platform].name}
竞品爆款标题：${hitTitles}
竞品关键词布局：${keywords}

要求：
1. 生成3条差异化对标内容标题
2. 每条标注差异化点（相比竞品的独特优势）
3. 标题需包含核心关键词但角度不同`;

        let result = "";
        if (RuntimeManager.isInitialized() && RuntimeManager.hasRuntime(RuntimeName.MediaStudio)) {
          const resp = await RuntimeManager.execute(RuntimeName.MediaStudio, "generate_custom", {
            type: "text",
            topic: `竞品对标：${comp.competitorName}`,
            prompt,
            opts: { counter: true },
          });
          const inner = resp?.data as any;
          if (resp?.success && inner) {
            result = inner.content || inner.text || "";
          }
        }

        // 解析生成结果或使用默认对标策略
        const counterContents: CounterContent[] = result
          ? [
              {
                title: `${result.split("\n")[0].replace(/^\d+[.、)\s]+/, "").slice(0, 50)}`,
                differentiation: "差异化角度1：从用户痛点切入，对比竞品泛泛而谈",
                status: "generated",
              },
              {
                title: `${result.split("\n")[1]?.replace(/^\d+[.、)\s]+/, "").slice(0, 50) || "差异化标题2"}`,
                differentiation: "差异化角度2：数据支撑 vs 竞品主观描述",
                status: "generated",
              },
              {
                title: `${result.split("\n")[2]?.replace(/^\d+[.、)\s]+/, "").slice(0, 50) || "差异化标题3"}`,
                differentiation: "差异化角度3：本地化服务 vs 竞品全国通用",
                status: "generated",
              },
            ]
          : [
              {
                title: "对标内容生成未完成",
                differentiation: "请确保 Runtime 已初始化后重试",
                status: "planned",
              },
            ];

        updateState((prev) => ({
          ...prev,
          competitors: prev.competitors.map((c) =>
            c.id === competitorId ? { ...c, counterContent: counterContents } : c
          ),
        }));
        setToast({ message: "差异化对标内容已生成", type: "success" });
      } catch (err) {
        setToast({ message: `生成失败: ${err instanceof Error ? err.message : "未知错误"}`, type: "error" });
      }
    },
    [state.competitors, updateState]
  );

  // ============================================
  // 创建内容计划
  // ============================================
  const handleCreatePlan = useCallback(() => {
    if (!createForm.title.trim()) {
      setToast({ message: "请输入内容标题", type: "error" });
      return;
    }
    if (createForm.selectedPlatforms.length === 0) {
      setToast({ message: "请至少选择一个分发平台", type: "error" });
      return;
    }
    const platformPlans: PlatformPlan[] = createForm.selectedPlatforms.map((plat) =>
      autoPlatformPlan(createForm.tier, plat, createForm.platformAccountTypes[plat])
    );
    const newPlan: ContentPlan = {
      id: genId(),
      tier: createForm.tier,
      title: createForm.title.trim(),
      targetKeywords: createForm.selectedKeywords,
      brandAssets: createForm.selectedAssets,
      format: createForm.format,
      platformPlans,
      status: "planned",
      createdAt: new Date().toISOString(),
    };
    updateState((prev) => ({ ...prev, contentPlans: [newPlan, ...prev.contentPlans] }));
    // 重置表单
    setCreateForm({
      tier: "industry_pain",
      title: "",
      format: "long_article",
      selectedKeywords: [],
      selectedAssets: [],
      selectedPlatforms: ["xiaohongshu"],
      platformAccountTypes: {
        wechat_official: "main",
        xiaohongshu: "sub",
        douyin: "sub",
        zhihu: "main",
        bilibili: "main",
      },
    });
    setShowCreateForm(false);
    setToast({ message: "内容计划已创建", type: "success" });
  }, [createForm, updateState]);

  // ============================================
  // 删除内容计划
  // ============================================
  const deletePlan = useCallback(
    (planId: string) => {
      updateState((prev) => ({
        ...prev,
        contentPlans: prev.contentPlans.filter((p) => p.id !== planId),
      }));
    },
    [updateState]
  );

  // ============================================
  // 矩阵账号 CRUD
  // ============================================
  const handleAddMatrixAccount = useCallback(() => {
    if (!matrixForm.accountName.trim()) {
      setToast({ message: "请输入账号名称", type: "error" });
      return;
    }
    updateState((prev) => ({
      ...prev,
      matrixAccounts: [...prev.matrixAccounts, { ...matrixForm, contentCount: 0, avgViews: 0 }],
    }));
    setMatrixForm({ platform: "xiaohongshu", accountType: "main", accountName: "", followers: 0, authority: 50 });
    setShowMatrixForm(false);
    setToast({ message: "矩阵账号已添加", type: "success" });
  }, [matrixForm, updateState]);

  const deleteMatrixAccount = useCallback(
    (idx: number) => {
      updateState((prev) => ({
        ...prev,
        matrixAccounts: prev.matrixAccounts.filter((_, i) => i !== idx),
      }));
    },
    [updateState]
  );

  // ============================================
  // 竞品 CRUD
  // ============================================
  const handleAddCompetitor = useCallback(() => {
    if (!competitorForm.competitorName.trim()) {
      setToast({ message: "请输入竞品名称", type: "error" });
      return;
    }
    updateState((prev) => ({
      ...prev,
      competitors: [
        ...prev.competitors,
        { id: genId(), competitorName: competitorForm.competitorName.trim(), platform: competitorForm.platform, hitContent: [] },
      ],
    }));
    setCompetitorForm({ competitorName: "", platform: "xiaohongshu" });
    setShowCompetitorForm(false);
    setToast({ message: "竞品已添加", type: "success" });
  }, [competitorForm, updateState]);

  const deleteCompetitor = useCallback(
    (compId: string) => {
      updateState((prev) => ({
        ...prev,
        competitors: prev.competitors.filter((c) => c.id !== compId),
      }));
    },
    [updateState]
  );

  const handleAddHitContent = useCallback(
    (compId: string) => {
      if (!hitContentForm.title.trim()) {
        setToast({ message: "请输入爆款标题", type: "error" });
        return;
      }
      updateState((prev) => ({
        ...prev,
        competitors: prev.competitors.map((c) =>
          c.id === compId
            ? {
                ...c,
                hitContent: [
                  ...c.hitContent,
                  {
                    title: hitContentForm.title.trim(),
                    titleStructure: hitContentForm.titleStructure.trim() || "未分析",
                    keywordLayout: hitContentForm.keywordLayout
                      .split(/[,，、\s]+/)
                      .filter(Boolean),
                    views: hitContentForm.views,
                    engagement: hitContentForm.engagement,
                  },
                ],
              }
            : c
        ),
      }));
      setHitContentForm({ title: "", titleStructure: "", keywordLayout: "", views: 0, engagement: 0 });
      setToast({ message: "爆款内容已添加", type: "success" });
    },
    [hitContentForm, updateState]
  );

  // ============================================
  // 统计数据
  // ============================================
  const tierStats = useMemo(() => {
    const stats: Record<ContentTier, { planned: number; generated: number; published: number; indexRate: number }> = {
      brand_deep: { planned: 0, generated: 0, published: 0, indexRate: 0 },
      business_precise: { planned: 0, generated: 0, published: 0, indexRate: 0 },
      industry_pain: { planned: 0, generated: 0, published: 0, indexRate: 0 },
    };
    state.contentPlans.forEach((p) => {
      stats[p.tier].planned++;
      if (["generated", "published", "indexed", "not_indexed"].includes(p.status)) stats[p.tier].generated++;
      if (["published", "indexed", "not_indexed"].includes(p.status)) stats[p.tier].published++;
    });
    (Object.keys(stats) as ContentTier[]).forEach((tier) => {
      const tierPlans = state.contentPlans.filter((p) => p.tier === tier);
      stats[tier].indexRate = calcIndexRate(tierPlans);
    });
    return stats;
  }, [state.contentPlans]);

  // 未收录内容列表
  const notIndexedPlans = useMemo(
    () =>
      state.contentPlans.filter(
        (p) =>
          p.status === "not_indexed" ||
          (p.indexedStatus && Object.values(p.indexedStatus).some((s) => s === "not_indexed"))
      ),
    [state.contentPlans]
  );

  // ============================================
  // 渲染：Toast
  // ============================================
  const renderToast = () => (
    <AnimatePresence>
      {toast && (
        <motion.div
          initial={{ opacity: 0, y: -20, x: "-50%" }}
          animate={{ opacity: 1, y: 0, x: "-50%" }}
          exit={{ opacity: 0, y: -20, x: "-50%" }}
          className="fixed top-6 left-1/2 z-50 flex items-center gap-2 rounded-lg border px-4 py-2 text-sm shadow-xl backdrop-blur-xl"
          style={{
            background: toast.type === "success" ? "rgba(16,185,129,0.15)" : "rgba(239,68,68,0.15)",
            borderColor: toast.type === "success" ? "rgba(16,185,129,0.3)" : "rgba(239,68,68,0.3)",
            color: toast.type === "success" ? "#10B981" : "#EF4444",
          }}
        >
          {toast.type === "success" ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
          {toast.message}
        </motion.div>
      )}
    </AnimatePresence>
  );

  // ============================================
  // 渲染：空状态
  // ============================================
  const renderEmpty = (text: string, icon: typeof Layers) => (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="mb-3 rounded-full border border-white/[0.06] bg-white/[0.02] p-4 backdrop-blur-xl">
        {(() => {
          const Icon = icon;
          return <Icon size={28} className="text-white/30" />;
        })()}
      </div>
      <p className="text-sm text-white/40">{text}</p>
    </div>
  );

  // ============================================
  // 渲染：内容梯队 Tab
  // ============================================
  const renderTiersTab = () => (
    <div className="space-y-6">
      {/* 三类内容梯队卡片 */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {(Object.keys(CONTENT_TIER_META) as ContentTier[]).map((tier) => {
          const meta = CONTENT_TIER_META[tier];
          const stats = tierStats[tier];
          const Icon = meta.icon;
          return (
            <motion.div
              key={tier}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5 backdrop-blur-xl"
              style={{ borderTop: `2px solid ${meta.color}` }}
            >
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div
                    className="flex h-9 w-9 items-center justify-center rounded-lg"
                    style={{ background: `${meta.color}20`, color: meta.color }}
                  >
                    <Icon size={18} />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-white">{meta.label}</h3>
                    <p className="text-xs text-white/40">{meta.frequency}</p>
                  </div>
                </div>
              </div>
              <p className="mb-4 text-xs leading-relaxed text-white/50">{meta.desc}</p>
              <div className="grid grid-cols-4 gap-2">
                <div className="rounded-lg border border-white/[0.04] bg-white/[0.01] p-2 text-center">
                  <p className="text-lg font-bold text-white">{stats.planned}</p>
                  <p className="text-[10px] text-white/40">计划数</p>
                </div>
                <div className="rounded-lg border border-white/[0.04] bg-white/[0.01] p-2 text-center">
                  <p className="text-lg font-bold" style={{ color: "#3B82F6" }}>
                    {stats.generated}
                  </p>
                  <p className="text-[10px] text-white/40">已生成</p>
                </div>
                <div className="rounded-lg border border-white/[0.04] bg-white/[0.01] p-2 text-center">
                  <p className="text-lg font-bold" style={{ color: "#10B981" }}>
                    {stats.published}
                  </p>
                  <p className="text-[10px] text-white/40">已发布</p>
                </div>
                <div className="rounded-lg border border-white/[0.04] bg-white/[0.01] p-2 text-center">
                  <p className="text-lg font-bold" style={{ color: DSO_COLOR.primary }}>
                    {stats.indexRate}%
                  </p>
                  <p className="text-[10px] text-white/40">收录率</p>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* 创建内容计划 */}
      <div className="flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-white">
          <Layers size={16} style={{ color: DSO_COLOR.primary }} />
          内容计划列表
        </h3>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="flex items-center gap-1.5 rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-1.5 text-xs text-white/80 backdrop-blur-xl transition hover:bg-white/[0.05]"
          style={{ borderColor: `${DSO_COLOR.primary}40` }}
        >
          <Plus size={14} style={{ color: DSO_COLOR.primary }} />
          新建内容计划
        </button>
      </div>

      {/* 创建表单 */}
      <AnimatePresence>
        {showCreateForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden rounded-xl border border-white/[0.06] bg-white/[0.02] p-5 backdrop-blur-xl"
          >
            <div className="space-y-4">
              {/* 梯队 + 形式 */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1.5 block text-xs text-white/50">内容梯队</label>
                  <select
                    value={createForm.tier}
                    onChange={(e) => setCreateForm({ ...createForm, tier: e.target.value as ContentTier })}
                    className="w-full rounded-lg border border-white/[0.06] bg-black/30 px-3 py-2 text-sm text-white outline-none"
                  >
                    {(Object.keys(CONTENT_TIER_META) as ContentTier[]).map((t) => (
                      <option key={t} value={t} className="bg-zinc-900">
                        {CONTENT_TIER_META[t].label} - {CONTENT_TIER_META[t].frequency}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1.5 block text-xs text-white/50">内容形式</label>
                  <select
                    value={createForm.format}
                    onChange={(e) => setCreateForm({ ...createForm, format: e.target.value as ContentFormat })}
                    className="w-full rounded-lg border border-white/[0.06] bg-black/30 px-3 py-2 text-sm text-white outline-none"
                  >
                    {(Object.keys(FORMAT_META) as ContentFormat[]).map((f) => (
                      <option key={f} value={f} className="bg-zinc-900">
                        {FORMAT_META[f].label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* 标题 */}
              <div>
                <label className="mb-1.5 block text-xs text-white/50">内容标题</label>
                <input
                  value={createForm.title}
                  onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })}
                  placeholder="输入内容标题（如：长沙装修避坑指南2026最新版）"
                  className="w-full rounded-lg border border-white/[0.06] bg-black/30 px-3 py-2 text-sm text-white outline-none placeholder:text-white/20"
                />
              </div>

              {/* GEO 关键词选择 */}
              <div>
                <label className="mb-1.5 block text-xs text-white/50">
                  关联 GEO 关键词
                  <span className="ml-2 text-white/30">（来自 GEO 输入层，共 {geoKeywords.length} 个）</span>
                </label>
                {geoKeywords.length === 0 ? (
                  <p className="rounded-lg border border-white/[0.04] bg-white/[0.01] px-3 py-2 text-xs text-white/30">
                    暂无 GEO 关键词，请先在 GEO 输入层录入关键词
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {geoKeywords.map((kw) => {
                      const selected = createForm.selectedKeywords.includes(kw.keyword);
                      return (
                        <button
                          key={kw.id}
                          onClick={() =>
                            setCreateForm({
                              ...createForm,
                              selectedKeywords: selected
                                ? createForm.selectedKeywords.filter((k) => k !== kw.keyword)
                                : [...createForm.selectedKeywords, kw.keyword],
                            })
                          }
                          className="rounded-full border px-3 py-1 text-xs transition"
                          style={{
                            borderColor: selected ? DSO_COLOR.primary : "rgba(255,255,255,0.08)",
                            background: selected ? `${DSO_COLOR.primary}20` : "rgba(255,255,255,0.02)",
                            color: selected ? DSO_COLOR.primary : "rgba(255,255,255,0.6)",
                          }}
                        >
                          <Hash size={10} className="mr-1 inline" />
                          {kw.keyword}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* 品牌资产选择 */}
              <div>
                <label className="mb-1.5 block text-xs text-white/50">
                  关联企业资产
                  <span className="ml-2 text-white/30">（来自 GEO 品牌池，共 {brandAssets.length} 个）</span>
                </label>
                {brandAssets.length === 0 ? (
                  <p className="rounded-lg border border-white/[0.04] bg-white/[0.01] px-3 py-2 text-xs text-white/30">
                    暂无品牌资产，请先在 GEO 输入层录入品牌信息
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {brandAssets.map((asset) => {
                      const selected = createForm.selectedAssets.includes(asset);
                      return (
                        <button
                          key={asset}
                          onClick={() =>
                            setCreateForm({
                              ...createForm,
                              selectedAssets: selected
                                ? createForm.selectedAssets.filter((a) => a !== asset)
                                : [...createForm.selectedAssets, asset],
                            })
                          }
                          className="rounded-full border px-3 py-1 text-xs transition"
                          style={{
                            borderColor: selected ? "#A855F7" : "rgba(255,255,255,0.08)",
                            background: selected ? "rgba(168,85,247,0.15)" : "rgba(255,255,255,0.02)",
                            color: selected ? "#C084FC" : "rgba(255,255,255,0.6)",
                          }}
                        >
                          <Award size={10} className="mr-1 inline" />
                          {asset}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* 平台选择 + 账号类型 */}
              <div>
                <label className="mb-1.5 block text-xs text-white/50">分发平台（选择并设置主/子账号）</label>
                <div className="space-y-2">
                  {ALL_PLATFORMS.map((plat) => {
                    const meta = PLATFORM_META[plat];
                    const selected = createForm.selectedPlatforms.includes(plat);
                    return (
                      <div
                        key={plat}
                        className="flex items-center gap-3 rounded-lg border border-white/[0.04] bg-white/[0.01] px-3 py-2"
                      >
                        <button
                          onClick={() =>
                            setCreateForm({
                              ...createForm,
                              selectedPlatforms: selected
                                ? createForm.selectedPlatforms.filter((p) => p !== plat)
                                : [...createForm.selectedPlatforms, plat],
                            })
                          }
                          className="flex items-center gap-2 text-sm"
                          style={{ color: selected ? meta.color : "rgba(255,255,255,0.4)" }}
                        >
                          <span
                            className="flex h-5 w-5 items-center justify-center rounded text-xs"
                            style={{
                              background: selected ? `${meta.color}20` : "transparent",
                              border: `1px solid ${selected ? meta.color : "rgba(255,255,255,0.1)"}`,
                            }}
                          >
                            {selected ? "✓" : ""}
                          </span>
                          <span>{meta.icon}</span>
                          <span>{meta.name}</span>
                        </button>
                        {selected && (
                          <div className="ml-auto flex items-center gap-1">
                            {(["main", "sub"] as const).map((at) => (
                              <button
                                key={at}
                                onClick={() =>
                                  setCreateForm({
                                    ...createForm,
                                    platformAccountTypes: {
                                      ...createForm.platformAccountTypes,
                                      [plat]: at,
                                    },
                                  })
                                }
                                className="rounded px-2 py-0.5 text-xs transition"
                                style={{
                                  background:
                                    createForm.platformAccountTypes[plat] === at
                                      ? `${ACCOUNT_TYPE_META[at].color}20`
                                      : "transparent",
                                  color:
                                    createForm.platformAccountTypes[plat] === at
                                      ? ACCOUNT_TYPE_META[at].color
                                      : "rgba(255,255,255,0.3)",
                                  border: `1px solid ${
                                    createForm.platformAccountTypes[plat] === at
                                      ? ACCOUNT_TYPE_META[at].color
                                      : "rgba(255,255,255,0.08)"
                                  }`,
                                }}
                              >
                                {ACCOUNT_TYPE_META[at].label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* 操作按钮 */}
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowCreateForm(false)}
                  className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-4 py-2 text-xs text-white/60"
                >
                  取消
                </button>
                <button
                  onClick={handleCreatePlan}
                  className="rounded-lg px-4 py-2 text-xs font-medium text-white"
                  style={{ background: DSO_COLOR.gradient }}
                >
                  创建计划
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 内容计划列表 */}
      {state.contentPlans.length === 0
        ? renderEmpty("暂无内容计划，点击「新建内容计划」开始创建", FileText)
        : (
          <div className="space-y-3">
            {state.contentPlans.map((plan) => {
              const tierMeta = CONTENT_TIER_META[plan.tier];
              const statusMeta = STATUS_META[plan.status];
              const StatusIcon = statusMeta.icon;
              const FormatIcon = FORMAT_META[plan.format].icon;
              const isExpanded = expandedPlanId === plan.id;
              const isGenerating = generatingIds.has(plan.id);
              return (
                <div
                  key={plan.id}
                  className="rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl"
                >
                  {/* 计划头部 */}
                  <div
                    className="flex cursor-pointer items-start gap-3 p-4"
                    onClick={() => setExpandedPlanId(isExpanded ? null : plan.id)}
                  >
                    <div
                      className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
                      style={{ background: `${tierMeta.color}20`, color: tierMeta.color }}
                    >
                      <FormatIcon size={16} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span
                          className="rounded px-1.5 py-0.5 text-[10px] font-medium"
                          style={{ background: `${tierMeta.color}20`, color: tierMeta.color }}
                        >
                          {tierMeta.label}
                        </span>
                        <span
                          className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px]"
                          style={{ background: `${statusMeta.color}15`, color: statusMeta.color }}
                        >
                          <StatusIcon size={10} className={isGenerating ? "animate-spin" : ""} />
                          {statusMeta.label}
                        </span>
                        <span className="text-[10px] text-white/30">
                          {FORMAT_META[plan.format].label}
                        </span>
                      </div>
                      <h4 className="mt-1 truncate text-sm font-medium text-white">{plan.title}</h4>
                      <div className="mt-1 flex flex-wrap items-center gap-1.5">
                        {plan.targetKeywords.slice(0, 3).map((kw) => (
                          <span
                            key={kw}
                            className="rounded bg-white/[0.04] px-1.5 py-0.5 text-[10px] text-white/50"
                          >
                            #{kw}
                          </span>
                        ))}
                        {plan.targetKeywords.length > 3 && (
                          <span className="text-[10px] text-white/30">
                            +{plan.targetKeywords.length - 3}
                          </span>
                        )}
                        {plan.brandAssets.slice(0, 2).map((a) => (
                          <span
                            key={a}
                            className="rounded bg-purple-500/10 px-1.5 py-0.5 text-[10px] text-purple-300"
                          >
                            {a}
                          </span>
                        ))}
                      </div>
                    </div>
                    {isExpanded ? (
                      <ChevronDown size={16} className="text-white/30" />
                    ) : (
                      <ChevronRight size={16} className="text-white/30" />
                    )}
                  </div>

                  {/* 展开详情 */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden border-t border-white/[0.04]"
                      >
                        <div className="space-y-3 p-4">
                          {/* 平台分发计划 */}
                          <div>
                            <p className="mb-2 text-xs font-medium text-white/60">平台分发策略</p>
                            <div className="space-y-1.5">
                              {plan.platformPlans.map((pp) => {
                                const pMeta = PLATFORM_META[pp.platform];
                                const aMeta = ACCOUNT_TYPE_META[pp.accountType];
                                return (
                                  <div
                                    key={pp.platform}
                                    className="flex items-start gap-2 rounded-lg border border-white/[0.04] bg-white/[0.01] px-3 py-2"
                                  >
                                    <span className="text-sm">{pMeta.icon}</span>
                                    <div className="flex-1 text-xs">
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium text-white/70">{pMeta.name}</span>
                                        <span
                                          className="rounded px-1 py-0.5 text-[10px]"
                                          style={{ background: `${aMeta.color}20`, color: aMeta.color }}
                                        >
                                          {aMeta.label}
                                        </span>
                                      </div>
                                      <p className="mt-0.5 text-white/40">{pp.differentiatedAngle}</p>
                                      <p className="mt-0.5 text-[10px] text-white/30">
                                        风格：{pp.contentStyle} | 时段：{pp.publishTiming} | 频率：{pp.publishFrequency}
                                      </p>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          {/* 生成内容预览 */}
                          {plan.generatedContent && (
                            <div>
                              <p className="mb-2 text-xs font-medium text-white/60">生成内容预览</p>
                              <div className="max-h-48 overflow-y-auto rounded-lg border border-white/[0.04] bg-black/20 p-3 text-xs leading-relaxed text-white/60">
                                {plan.generatedContent}
                              </div>
                            </div>
                          )}

                          {/* 收录状态 */}
                          {plan.indexedStatus && (
                            <div>
                              <p className="mb-2 text-xs font-medium text-white/60">收录检测</p>
                              <div className="flex flex-wrap gap-2">
                                {plan.platformPlans.map((pp) => {
                                  const idx = plan.indexedStatus![pp.platform];
                                  const color =
                                    idx === "indexed" ? "#22C55E" : idx === "not_indexed" ? "#EF4444" : "#6B7280";
                                  return (
                                    <button
                                      key={pp.platform}
                                      onClick={() => toggleIndexStatus(plan.id, pp.platform)}
                                      className="flex items-center gap-1.5 rounded-lg border px-2 py-1 text-xs"
                                      style={{ borderColor: `${color}40`, color }}
                                    >
                                      <span>{PLATFORM_META[pp.platform].icon}</span>
                                      <span>{PLATFORM_META[pp.platform].name}</span>
                                      <span>· {idx === "indexed" ? "已收录" : idx === "not_indexed" ? "未收录" : "待检测"}</span>
                                    </button>
                                  );
                                })}
                              </div>
                              <p className="mt-1 text-[10px] text-white/30">点击状态标签切换收录状态（待检测 → 已收录 → 未收录）</p>
                            </div>
                          )}

                          {/* 操作按钮 */}
                          <div className="flex flex-wrap gap-2">
                            {plan.status === "planned" && (
                              <button
                                onClick={() => generateContent(plan.id)}
                                disabled={isGenerating}
                                className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
                                style={{ background: DSO_COLOR.gradient }}
                              >
                                {isGenerating ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                                {isGenerating ? "生成中..." : "生成内容"}
                              </button>
                            )}
                            {(plan.status === "generated" || plan.status === "failed") && (
                              <button
                                onClick={() => generateContent(plan.id)}
                                disabled={isGenerating}
                                className="flex items-center gap-1.5 rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-1.5 text-xs text-white/70"
                              >
                                <RefreshCw size={12} />
                                重新生成
                              </button>
                            )}
                            {(plan.status === "generated" || plan.status === "failed") && (
                              <button
                                onClick={() => publishPlan(plan.id)}
                                className="flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs"
                                style={{ borderColor: "#10B98140", color: "#10B981" }}
                              >
                                <Send size={12} />
                                发布
                              </button>
                            )}
                            {plan.status === "not_indexed" && (
                              <button
                                onClick={() => repostNotIndexed(plan.id)}
                                disabled={isGenerating}
                                className="flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs"
                                style={{ borderColor: "#EF444440", color: "#EF4444" }}
                              >
                                {isGenerating ? <Loader2 size={12} className="animate-spin" /> : <RotateCw size={12} />}
                                一键补发
                              </button>
                            )}
                            <button
                              onClick={() => deletePlan(plan.id)}
                              className="ml-auto flex items-center gap-1.5 rounded-lg border border-white/[0.06] px-3 py-1.5 text-xs text-white/40 hover:text-red-400"
                            >
                              <Trash2 size={12} />
                              删除
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        )}
    </div>
  );

  // ============================================
  // 渲染：矩阵分发 Tab
  // ============================================
  const renderMatrixTab = () => (
    <div className="space-y-6">
      {/* 矩阵账号表 */}
      <div className="flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-white">
          <Users size={16} style={{ color: DSO_COLOR.primary }} />
          矩阵账号管理（5 平台 × 主/子账号）
        </h3>
        <button
          onClick={() => setShowMatrixForm(!showMatrixForm)}
          className="flex items-center gap-1.5 rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-1.5 text-xs text-white/80 backdrop-blur-xl"
          style={{ borderColor: `${DSO_COLOR.primary}40` }}
        >
          <Plus size={14} style={{ color: DSO_COLOR.primary }} />
          添加矩阵账号
        </button>
      </div>

      {/* 添加矩阵账号表单 */}
      <AnimatePresence>
        {showMatrixForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 backdrop-blur-xl"
          >
            <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
              <select
                value={matrixForm.platform}
                onChange={(e) => setMatrixForm({ ...matrixForm, platform: e.target.value as DsoPlatform })}
                className="rounded-lg border border-white/[0.06] bg-black/30 px-2 py-2 text-xs text-white outline-none"
              >
                {ALL_PLATFORMS.map((p) => (
                  <option key={p} value={p} className="bg-zinc-900">
                    {PLATFORM_META[p].name}
                  </option>
                ))}
              </select>
              <select
                value={matrixForm.accountType}
                onChange={(e) => setMatrixForm({ ...matrixForm, accountType: e.target.value as "main" | "sub" })}
                className="rounded-lg border border-white/[0.06] bg-black/30 px-2 py-2 text-xs text-white outline-none"
              >
                <option value="main" className="bg-zinc-900">主账号</option>
                <option value="sub" className="bg-zinc-900">子账号</option>
              </select>
              <input
                value={matrixForm.accountName}
                onChange={(e) => setMatrixForm({ ...matrixForm, accountName: e.target.value })}
                placeholder="账号名称"
                className="rounded-lg border border-white/[0.06] bg-black/30 px-2 py-2 text-xs text-white outline-none placeholder:text-white/20"
              />
              <input
                type="number"
                value={matrixForm.followers}
                onChange={(e) => setMatrixForm({ ...matrixForm, followers: Number(e.target.value) })}
                placeholder="粉丝数"
                className="rounded-lg border border-white/[0.06] bg-black/30 px-2 py-2 text-xs text-white outline-none placeholder:text-white/20"
              />
              <div className="flex gap-2">
                <input
                  type="number"
                  value={matrixForm.authority}
                  onChange={(e) => setMatrixForm({ ...matrixForm, authority: Number(e.target.value) })}
                  placeholder="权重"
                  className="w-20 rounded-lg border border-white/[0.06] bg-black/30 px-2 py-2 text-xs text-white outline-none placeholder:text-white/20"
                />
                <button
                  onClick={handleAddMatrixAccount}
                  className="rounded-lg px-3 py-2 text-xs font-medium text-white"
                  style={{ background: DSO_COLOR.gradient }}
                >
                  添加
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 矩阵表 */}
      {state.matrixAccounts.length === 0
        ? renderEmpty("暂无矩阵账号，点击「添加矩阵账号」开始配置", Users)
        : (
          <div className="overflow-x-auto rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-white/[0.06] text-white/40">
                  <th className="px-4 py-3 font-medium">平台</th>
                  <th className="px-4 py-3 font-medium">账号类型</th>
                  <th className="px-4 py-3 font-medium">账号名称</th>
                  <th className="px-4 py-3 font-medium">粉丝数</th>
                  <th className="px-4 py-3 font-medium">账号权重</th>
                  <th className="px-4 py-3 font-medium">内容数</th>
                  <th className="px-4 py-3 font-medium">均阅读</th>
                  <th className="px-4 py-3 font-medium">操作</th>
                </tr>
              </thead>
              <tbody>
                {state.matrixAccounts.map((acc, idx) => {
                  const pMeta = PLATFORM_META[acc.platform];
                  const aMeta = ACCOUNT_TYPE_META[acc.accountType];
                  return (
                    <tr key={idx} className="border-b border-white/[0.03] hover:bg-white/[0.01]">
                      <td className="px-4 py-3">
                        <span style={{ color: pMeta.color }}>
                          {pMeta.icon} {pMeta.name}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className="rounded px-1.5 py-0.5 text-[10px]"
                          style={{ background: `${aMeta.color}20`, color: aMeta.color }}
                        >
                          {aMeta.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-white/70">{acc.accountName}</td>
                      <td className="px-4 py-3 text-white/50">{acc.followers.toLocaleString()}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 w-16 overflow-hidden rounded-full bg-white/10">
                            <div
                              className="h-full rounded-full"
                              style={{
                                width: `${acc.authority}%`,
                                background: DSO_COLOR.gradient,
                              }}
                            />
                          </div>
                          <span className="text-white/50">{acc.authority}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-white/50">{acc.contentCount}</td>
                      <td className="px-4 py-3 text-white/50">{acc.avgViews.toLocaleString()}</td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => deleteMatrixAccount(idx)}
                          className="text-white/30 hover:text-red-400"
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

      {/* 平台差异化策略 */}
      <div>
        <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-white">
          <Target size={16} style={{ color: DSO_COLOR.primary }} />
          平台差异化分发策略
        </h3>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {ALL_PLATFORMS.map((plat) => {
            const meta = PLATFORM_META[plat];
            const accounts = state.matrixAccounts.filter((a) => a.platform === plat);
            return (
              <div
                key={plat}
                className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 backdrop-blur-xl"
                style={{ borderTop: `2px solid ${meta.color}` }}
              >
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm font-medium text-white">
                    {meta.icon} {meta.name}
                  </span>
                  <span className="text-xs text-white/40">{accounts.length} 个账号</span>
                </div>
                <p className="mb-2 text-xs text-white/50">{meta.algorithm}</p>
                <div className="space-y-1">
                  <p className="text-[10px] text-white/40">
                    字数上限：{meta.charLimit.toLocaleString()}
                  </p>
                  {accounts.map((a, i) => (
                    <div key={i} className="flex items-center justify-between text-[10px]">
                      <span style={{ color: ACCOUNT_TYPE_META[a.accountType].color }}>
                        {ACCOUNT_TYPE_META[a.accountType].label}
                      </span>
                      <span className="text-white/50">{a.accountName}</span>
                    </div>
                  ))}
                  {accounts.length === 0 && (
                    <p className="text-[10px] text-white/25">未配置账号</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 收录检测面板 */}
      <div>
        <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-white">
          <Search size={16} style={{ color: DSO_COLOR.primary }} />
          收录检测面板
          {notIndexedPlans.length > 0 && (
            <span
              className="rounded-full px-2 py-0.5 text-[10px]"
              style={{ background: "#EF444420", color: "#EF4444" }}
            >
              {notIndexedPlans.length} 条未收录
            </span>
          )}
        </h3>
        {notIndexedPlans.length === 0 ? (
          renderEmpty("暂无未收录内容，发布内容后将显示收录检测", CheckCircle2)
        ) : (
          <div className="space-y-2">
            {notIndexedPlans.map((plan) => {
              const notIndexedPlats = plan.platformPlans.filter(
                (pp) => plan.indexedStatus?.[pp.platform] === "not_indexed"
              );
              return (
                <div
                  key={plan.id}
                  className="flex items-center gap-3 rounded-xl border border-red-500/20 bg-red-500/[0.03] p-3 backdrop-blur-xl"
                >
                  <AlertCircle size={16} className="shrink-0 text-red-400" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-white/80">{plan.title}</p>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {notIndexedPlats.map((pp) => (
                        <span
                          key={pp.platform}
                          className="rounded bg-red-500/10 px-1.5 py-0.5 text-[10px] text-red-400"
                        >
                          {PLATFORM_META[pp.platform].icon} {PLATFORM_META[pp.platform].name} 未收录
                        </span>
                      ))}
                    </div>
                  </div>
                  <button
                    onClick={() => repostNotIndexed(plan.id)}
                    disabled={generatingIds.has(plan.id)}
                    className="flex shrink-0 items-center gap-1.5 rounded-lg border border-red-500/30 px-3 py-1.5 text-xs text-red-400 disabled:opacity-50"
                  >
                    {generatingIds.has(plan.id) ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <RotateCw size={12} />
                    )}
                    一键补发
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  // ============================================
  // 渲染：竞品监测 Tab
  // ============================================
  const renderCompetitorTab = () => (
    <div className="space-y-6">
      {/* 竞品列表 */}
      <div className="flex items-center justify-between">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-white">
          <Swords size={16} style={{ color: DSO_COLOR.primary }} />
          竞品 DSO 监测
        </h3>
        <button
          onClick={() => setShowCompetitorForm(!showCompetitorForm)}
          className="flex items-center gap-1.5 rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-1.5 text-xs text-white/80 backdrop-blur-xl"
          style={{ borderColor: `${DSO_COLOR.primary}40` }}
        >
          <Plus size={14} style={{ color: DSO_COLOR.primary }} />
          添加竞品
        </button>
      </div>

      {/* 添加竞品表单 */}
      <AnimatePresence>
        {showCompetitorForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 backdrop-blur-xl"
          >
            <div className="grid grid-cols-2 gap-3">
              <input
                value={competitorForm.competitorName}
                onChange={(e) => setCompetitorForm({ ...competitorForm, competitorName: e.target.value })}
                placeholder="竞品名称（如：XX装修公司）"
                className="rounded-lg border border-white/[0.06] bg-black/30 px-3 py-2 text-sm text-white outline-none placeholder:text-white/20"
              />
              <div className="flex gap-2">
                <select
                  value={competitorForm.platform}
                  onChange={(e) => setCompetitorForm({ ...competitorForm, platform: e.target.value as DsoPlatform })}
                  className="flex-1 rounded-lg border border-white/[0.06] bg-black/30 px-3 py-2 text-sm text-white outline-none"
                >
                  {ALL_PLATFORMS.map((p) => (
                    <option key={p} value={p} className="bg-zinc-900">
                      {PLATFORM_META[p].name}
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleAddCompetitor}
                  className="rounded-lg px-4 py-2 text-sm font-medium text-white"
                  style={{ background: DSO_COLOR.gradient }}
                >
                  添加
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 竞品列表 */}
      {state.competitors.length === 0
        ? renderEmpty("暂无竞品监测数据，点击「添加竞品」开始", Swords)
        : (
          <div className="space-y-4">
            {state.competitors.map((comp) => {
              const pMeta = PLATFORM_META[comp.platform];
              const isEditing = editingCompetitorId === comp.id;
              return (
                <div
                  key={comp.id}
                  className="rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl"
                >
                  {/* 竞品头部 */}
                  <div className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-3">
                      <div
                        className="flex h-9 w-9 items-center justify-center rounded-lg"
                        style={{ background: `${DSO_COLOR.primary}20`, color: DSO_COLOR.primary }}
                      >
                        <Swords size={16} />
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-white">{comp.competitorName}</h4>
                        <p className="text-xs" style={{ color: pMeta.color }}>
                          {pMeta.icon} {pMeta.name} | {comp.hitContent.length} 条爆款
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setEditingCompetitorId(isEditing ? null : comp.id)}
                        className="flex items-center gap-1.5 rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-1.5 text-xs text-white/70"
                      >
                        <Plus size={12} />
                        添加爆款
                      </button>
                      {comp.hitContent.length > 0 && (
                        <button
                          onClick={() => generateCounterContent(comp.id)}
                          className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium text-white"
                          style={{ background: DSO_COLOR.gradient }}
                        >
                          <Sparkles size={12} />
                          生成对标内容
                        </button>
                      )}
                      <button
                        onClick={() => deleteCompetitor(comp.id)}
                        className="text-white/30 hover:text-red-400"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>

                  {/* 添加爆款内容表单 */}
                  <AnimatePresence>
                    {isEditing && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden border-t border-white/[0.04] p-4"
                      >
                        <div className="space-y-3">
                          <input
                            value={hitContentForm.title}
                            onChange={(e) => setHitContentForm({ ...hitContentForm, title: e.target.value })}
                            placeholder="爆款标题"
                            className="w-full rounded-lg border border-white/[0.06] bg-black/30 px-3 py-2 text-xs text-white outline-none placeholder:text-white/20"
                          />
                          <input
                            value={hitContentForm.titleStructure}
                            onChange={(e) => setHitContentForm({ ...hitContentForm, titleStructure: e.target.value })}
                            placeholder="标题结构分析（如：痛点词+数字+地域+解决方案）"
                            className="w-full rounded-lg border border-white/[0.06] bg-black/30 px-3 py-2 text-xs text-white outline-none placeholder:text-white/20"
                          />
                          <input
                            value={hitContentForm.keywordLayout}
                            onChange={(e) => setHitContentForm({ ...hitContentForm, keywordLayout: e.target.value })}
                            placeholder="关键词布局（逗号分隔，如：长沙装修,装修报价,装修避坑）"
                            className="w-full rounded-lg border border-white/[0.06] bg-black/30 px-3 py-2 text-xs text-white outline-none placeholder:text-white/20"
                          />
                          <div className="flex gap-3">
                            <input
                              type="number"
                              value={hitContentForm.views}
                              onChange={(e) => setHitContentForm({ ...hitContentForm, views: Number(e.target.value) })}
                              placeholder="浏览量"
                              className="flex-1 rounded-lg border border-white/[0.06] bg-black/30 px-3 py-2 text-xs text-white outline-none placeholder:text-white/20"
                            />
                            <input
                              type="number"
                              value={hitContentForm.engagement}
                              onChange={(e) => setHitContentForm({ ...hitContentForm, engagement: Number(e.target.value) })}
                              placeholder="互动量"
                              className="flex-1 rounded-lg border border-white/[0.06] bg-black/30 px-3 py-2 text-xs text-white outline-none placeholder:text-white/20"
                            />
                            <button
                              onClick={() => handleAddHitContent(comp.id)}
                              className="rounded-lg px-4 py-2 text-xs font-medium text-white"
                              style={{ background: DSO_COLOR.gradient }}
                            >
                              添加
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* 爆款拆解分析 */}
                  {comp.hitContent.length > 0 && (
                    <div className="border-t border-white/[0.04] p-4">
                      <p className="mb-3 flex items-center gap-1.5 text-xs font-medium text-white/60">
                        <BarChart3 size={12} />
                        爆款拆解分析
                      </p>
                      <div className="space-y-2">
                        {comp.hitContent.map((hit, i) => (
                          <div
                            key={i}
                            className="rounded-lg border border-white/[0.04] bg-white/[0.01] p-3"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <p className="text-sm text-white/80">{hit.title}</p>
                              <div className="flex shrink-0 gap-3 text-[10px] text-white/40">
                                <span className="flex items-center gap-1">
                                  <Eye size={10} />
                                  {hit.views.toLocaleString()}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Zap size={10} />
                                  {hit.engagement.toLocaleString()}
                                </span>
                              </div>
                            </div>
                            <p className="mt-1 text-[10px] text-white/40">
                              结构：{hit.titleStructure}
                            </p>
                            <div className="mt-1 flex flex-wrap gap-1">
                              {hit.keywordLayout.map((kw, j) => (
                                <span
                                  key={j}
                                  className="rounded bg-white/[0.04] px-1.5 py-0.5 text-[10px] text-white/50"
                                >
                                  #{kw}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* 差异化对标内容 */}
                  {comp.counterContent && comp.counterContent.length > 0 && (
                    <div className="border-t border-white/[0.04] p-4">
                      <p className="mb-3 flex items-center gap-1.5 text-xs font-medium text-white/60">
                        <Sparkles size={12} style={{ color: DSO_COLOR.primary }} />
                        差异化对标内容
                      </p>
                      <div className="space-y-2">
                        {comp.counterContent.map((cc, i) => (
                          <div
                            key={i}
                            className="rounded-lg border p-3"
                            style={{
                              borderColor: `${DSO_COLOR.primary}30`,
                              background: `${DSO_COLOR.primary}08`,
                            }}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <p className="text-sm font-medium" style={{ color: DSO_COLOR.accent }}>
                                {cc.title}
                              </p>
                              <span
                                className="rounded px-1.5 py-0.5 text-[10px]"
                                style={{
                                  background:
                                    cc.status === "generated" ? "#10B98120" : "#6B728020",
                                  color: cc.status === "generated" ? "#10B981" : "#6B7280",
                                }}
                              >
                                {cc.status === "generated" ? "已生成" : "待生成"}
                              </span>
                            </div>
                            <p className="mt-1 text-xs text-white/50">{cc.differentiation}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
    </div>
  );

  // ============================================
  // 渲染主界面
  // ============================================
  const tabs = [
    { key: "tiers" as const, label: "内容梯队", icon: Layers },
    { key: "matrix" as const, label: "矩阵分发", icon: Share2 },
    { key: "competitor" as const, label: "竞品监测", icon: Swords },
  ];

  return (
    <div className="min-h-screen bg-black p-6 text-white">
      {renderToast()}

      {/* 顶部标题栏 */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6"
      >
        <div className="flex items-center gap-3">
          <div
            className="flex h-11 w-11 items-center justify-center rounded-xl"
            style={{ background: DSO_COLOR.gradient }}
          >
            <Layers size={22} className="text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">
              DSO 决策层重构
              <span className="ml-2 text-xs font-normal text-white/40">
                GEO+DSO 商业化改造 · 第 3 层
              </span>
            </h1>
            <p className="text-xs text-white/40">
              多平台搜索卡位分发中枢 · 三类内容梯队 × 5 平台矩阵 × 竞品监测
            </p>
          </div>
        </div>
      </motion.div>

      {/* Tab 切换 */}
      <div className="mb-6 flex gap-2">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className="flex items-center gap-2 rounded-lg border px-4 py-2 text-sm transition backdrop-blur-xl"
              style={{
                borderColor: isActive ? `${DSO_COLOR.primary}60` : "rgba(255,255,255,0.06)",
                background: isActive ? `${DSO_COLOR.primary}15` : "rgba(255,255,255,0.02)",
                color: isActive ? DSO_COLOR.primary : "rgba(255,255,255,0.5)",
              }}
            >
              <Icon size={15} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab 内容 */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
      >
        {activeTab === "tiers" && renderTiersTab()}
        {activeTab === "matrix" && renderMatrixTab()}
        {activeTab === "competitor" && renderCompetitorTab()}
      </motion.div>

      {/* GEO 联动提示 */}
      {geoKeywords.length === 0 && brandAssets.length === 0 && activeTab === "tiers" && (
        <div className="mt-6 flex items-center gap-2 rounded-lg border border-orange-500/20 bg-orange-500/[0.03] p-3 text-xs text-orange-300/70">
          <AlertCircle size={14} />
          未检测到 GEO 输入层数据，内容计划将无法关联关键词和企业资产。请先在「GEO 输入层」录入数据。
        </div>
      )}
    </div>
  );
}
